import { useState } from "react";

import type { EventType } from "../eventsApi";
import { useLanguage } from "../i18n";
import { useLogger } from "../logger";
import type { Product } from "../productsApi";
import { occurredAt, WhenRow } from "./WhenRow";
import { SoilBar } from "./SoilBar";
import type { LastSoil } from "../tasksApi";

/**
 * Logging lives here, on the row, not behind a navigation.
 *
 * Nine plants used to mean nine round trips into a detail page. The evidence on
 * self-tracking abandonment puts capture cost at the top of the list of reasons
 * people stop, so the whole interaction is one tap from the list the app
 * already shows you.
 */

// Ordered by how often they actually happen, not alphabetically. A flat,
// meaningfully ordered list beats nesting: the cost is the ordering, not the
// count.
const MORE: Array<{ type: EventType; key: "actPestInspect" | "actRepot" | "actPrune" | "actMist" | "actRotate" | "actPropagate" | "actNote" }> = [
  { type: "pest_inspect", key: "actPestInspect" },
  { type: "repot", key: "actRepot" },
  { type: "prune", key: "actPrune" },
  { type: "mist", key: "actMist" },
  { type: "rotate", key: "actRotate" },
  { type: "propagate", key: "actPropagate" },
  { type: "note", key: "actNote" },
];

export function QuickLog({
  specimenId,
  plantName,
  products,
  dueDoses = {},
  leadWith = "water",
  checkDue = true,
  lastSoil,
}: {
  specimenId: string;
  plantName: string;
  products: Product[];
  /** Pot dose per product that is actually due today, keyed by product id. */
  dueDoses?: Record<string, string>;
  /** Which action the app is actually asking for, and so which one is filled. */
  leadWith?: "water" | "feed";
  /** Whether a Check is due. When it is not, the soil bar starts folded. */
  checkDue?: boolean;
  /** What the app last learned about this pot, shown while the bar is folded. */
  lastSoil?: LastSoil;
}) {
  const { t } = useLanguage();
  const { log, pendingFor } = useLogger();
  const [more, setMore] = useState(false);
  const [feeding, setFeeding] = useState(false);
  const [whenIndex, setWhenIndex] = useState(0);

  const pending = pendingFor(specimenId);
  const reading = pending.find((p) => p.type === "check");
  const watered = pending.some((p) => p.type === "water");
  const fed = pending.some((p) => p.type === "feed");

  // Auto-timestamped unless the owner says otherwise. The fast path never shows
  // a date control at all.
  function record(type: EventType, payload: Record<string, unknown>, label: string) {
    log(specimenId, type, payload, `${plantName} — ${label}`, occurredAt(whenIndex));
  }

  const soilFound = reading?.payload.soil as string | undefined;
  const foundDry = soilFound === "dry" || soilFound === "very_dry";

  return (
    <div className="quicklog">
      <SoilBar
        found={soilFound}
        lastSoil={lastSoil}
        askedFor={checkDue}
        onChoose={(value, label) => record("check", { soil: value }, label)}
      />

      {/* The loop from the guide: a dry reading is the moment to water, so the
          app offers it rather than making the owner find it. It is the engine's
          own voice, so it wears the engine's colour. */}
      {foundDry && !watered && (
        <div className="follow-through">
          <div className="follow-text">
            <div className="follow-title">{t.waterItNow}</div>
            <div className="follow-body">{t.waterItNowBody}</div>
          </div>
          <button type="button" className="chip" onClick={() => record("water", {}, t.actWater)}>
            {t.actWater}
          </button>
        </div>
      )}

      {/*
        Both actions keep the same place in the row whichever is being asked
        for — the muscle memory is the layout, not the emphasis. Only the fill
        moves, and it marks the one thing the app is asking for.
      */}
      <div className="chips actions" data-lead={leadWith}>
        <button
          type="button"
          className="chip primary"
          aria-pressed={watered}
          onClick={() => record("water", {}, t.actWater)}
        >
          {t.actWater}
        </button>

        <button
          type="button"
          className="chip primary"
          aria-pressed={fed}
          onClick={() => setFeeding((open) => !open)}
        >
          {t.actFeed}
        </button>

        {/* Everything else a person can do to a plant. It is a door, not an
            action, so it takes only the width it needs and carries no word. */}
        <button
          type="button"
          className="chip disclosure"
          aria-expanded={more}
          aria-label={t.more}
          onClick={() => setMore((open) => !open)}
        >
          <span aria-hidden="true">···</span>
        </button>
      </div>

      {/* Feeding always names the product. Without it the engine cannot match
          the feed to a schedule, so it would keep asking and the separation
          between the two products would never start. */}
      {feeding && (
        <div className="chips feed-picker">
          {products.map((product) => {
            const dose = dueDoses[product.id];
            return (
              <button
                key={product.id}
                type="button"
                className="chip"
                onClick={() => {
                  record("feed", { product_id: product.id, dose }, `${t.actFeed} ${product.name}`);
                  setFeeding(false);
                }}
              >
                {product.name}
                {dose && <span className="chip-note">{dose}</span>}
              </button>
            );
          })}
        </div>
      )}

      {more && (
        <div className="chips more-actions">
          {MORE.map((action) => (
            <button
              key={action.type}
              type="button"
              className="chip"
              onClick={() => {
                record(action.type, {}, t[action.key]);
                setMore(false);
              }}
            >
              {t[action.key]}
            </button>
          ))}
        </div>
      )}

      {(more || feeding) && <WhenRow index={whenIndex} onChange={setWhenIndex} />}
    </div>
  );
}
